import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const INTERACTIVE_TAGS = new Set(['input', 'textarea', 'select', 'button']);

export const DELPHI_APP_PATH = '/tools/delphi' as const;
export type SecretAppShortcutTarget = typeof DELPHI_APP_PATH | '/tools/value-calculator';

export function isPathWithin(pathname: string, basePath: string): boolean {
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

export function isTypingInInteractiveElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return INTERACTIVE_TAGS.has(tag) || Boolean(target.isContentEditable);
}

/**
 * Resolve a document-level shortcut to a mini-app route, or null if no match.
 * Exported for unit tests.
 */
export function resolveSecretAppShortcut(e: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'target'>): SecretAppShortcutTarget | null {
  // Require Shift so THESE navigation shortcuts never clobber the browser's
  // native in-page find. (The separate global master-search palette,
  // hooks/useGlobalMasterSearchShortcut.ts, deliberately DOES bind bare
  // Cmd/Ctrl+K, matching Delphi's own search which stays on its own
  // Cmd/Ctrl+F.) Alt is excluded to leave OS/text shortcuts untouched.
  if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return null;
  if (isTypingInInteractiveElement(e.target)) return null;

  if (e.key === 'f' || e.key === 'F') return DELPHI_APP_PATH;
  if (e.key === 'g' || e.key === 'G') return '/tools/value-calculator';
  return null;
}

/**
 * Power-user shortcuts: Cmd+Shift+F → Delphi, Cmd+Shift+G → Value Calculator.
 * Mounted once in AuthenticatedLayout so they work across every signed-in route.
 */
export function useSecretAppShortcuts(): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = resolveSecretAppShortcut(e);
      if (!target) return;
      if (location.pathname === target || location.pathname.startsWith(`${target}/`)) return;

      e.preventDefault();
      navigate(target);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [location.pathname, navigate]);
}
